import apiClient from '@/lib/apiClient';
import { ApiResponse } from '@/types';

export type EmailLogStatus =
  'pending' | 'queued' | 'sent' | 'delivered' | 'bounced' | 'failed' | 'complained';

export type EmailRecipientType = 'C' | 'E';

export interface EmailLogRecord {
  id: number;
  senderId: number;
  senderName: string | null;
  senderEmail: string | null;
  // Actual From address used to send (dept/employee sender picked by the RM).
  fromEmail: string | null;
  senderEmployeeCode: string | null;
  recipientType: EmailRecipientType | null;
  recipientId: number | null;
  recipientName: string | null;
  leadId: number | null;
  toAddresses: string[];
  ccAddresses: string[] | null;
  bccAddresses: string[] | null;
  replyTo: string | null;
  referenceNumber: string | null;
  subject: string;
  templateName: string | null;
  templateSlug: string | null;
  status: EmailLogStatus;
  sentAt: string | null;
  createdAt: string;
}

export interface EmailLogsParams {
  page?: number;
  limit?: number;
  search?: string;
  senderId?: number;
  recipientType?: EmailRecipientType;
  recipientId?: number;
  leadId?: number;
  status?: EmailLogStatus;
  // Comma-separated template slugs to match on.
  templateSlug?: string;
  from?: string;
  to?: string;
}

export interface EmailLogsResponse {
  rows: EmailLogRecord[];
  total: number;
  totalPages: number;
  currentPage: number;
}

const toParams = (params: EmailLogsParams): Record<string, string | number> => {
  const out: Record<string, string | number> = {};
  (Object.keys(params) as (keyof EmailLogsParams)[]).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === '') return;
    out[key] = value;
  });
  return out;
};

export const emailLogService = {
  list: async (params: EmailLogsParams = {}): Promise<EmailLogsResponse> => {
    const { data } = await apiClient.get<ApiResponse<EmailLogRecord[]>>('/email-logs', {
      params: toParams(params),
    });
    return {
      rows: data.data ?? [],
      total: (data.meta?.total as number) ?? 0,
      totalPages: (data.meta?.totalPages as number) ?? 1,
      currentPage: (data.meta?.currentPage as number) ?? 1,
    };
  },
};
